import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { KbStatusRepo } from '../lib/kb-status';
import { absolute, since } from '../lib/time';
import { UNSET } from '../lib/unset';
import { DataTable, type DataTableColumn } from './DataTable';
import { EmptyState } from './EmptyState';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';

// The Repos page's KB freshness table: which of this account's repos carry a
// knowledge base, how many pages it holds, when it was last imported, when the
// gardener last ran over it, and whether background KB work is policy-enabled
// for it.
//
// A freshness table — so a stale repo reads stale ("3d") without SSH or psql.
// Compact `since` in the cell, the full timestamp on hover.
//
// Background is a NEUTRAL pill both ways (#6568 owner note): "On" is a setting,
// not a settled success, and healthy is never green on these pages.
//
// Presenter; the container hands it the resolved rows.

/** Compact age with the full timestamp on hover, or a dash when never run. */
function freshness(iso: string | null): JSX.Element {
  return (
    <Show when={iso} fallback={<span>{UNSET}</span>}>
      {(at) => <span title={absolute(at())}>{since(at())}</span>}
    </Show>
  );
}

export function ReposKbPanel(props: { repos: readonly KbStatusRepo[] }): JSX.Element {
  const columns: readonly DataTableColumn<KbStatusRepo>[] = [
    {
      key: 'repo',
      rowHeader: true,
      header: t('repos.kb_col_repo'),
      mono: true,
      cell: (r) => r.repo_full_name,
    },
    { key: 'pages', header: t('repos.kb_col_pages'), mono: true, cell: (r) => r.page_count },
    {
      key: 'import',
      header: t('repos.kb_col_import'),
      cell: (r) => freshness(r.last_import_iso),
    },
    {
      key: 'gardener',
      header: t('repos.kb_col_gardener'),
      cell: (r) => freshness(r.last_gardener_iso),
    },
    {
      key: 'background',
      header: t('repos.kb_col_background'),
      cell: (r) => (
        <StatusPill tone="neutral">
          {r.background_enabled ? t('repos.kb_background_on') : t('repos.kb_background_off')}
        </StatusPill>
      ),
    },
  ];

  return (
    <Panel title={t('repos.kb_heading')}>
      <Show when={props.repos.length > 0} fallback={<EmptyState message={t('repos.kb_empty')} />}>
        <DataTable
          label={t('repos.kb_table_label')}
          columns={columns}
          rows={props.repos}
          rowKey={(r) => r.repo_id}
        />
      </Show>
    </Panel>
  );
}
